import { c as _c } from "react/compiler-runtime";
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import Grid from '../../../Grid';
import { ChevronDownIcon } from '../../../icons/ChevronDownIcon';
import { LinkIcon } from '../../../icons/LinkIcon';
import Typography from '../../../Typography';
import { type CardActionSignature, type NonActionThinkingSignature, constructThinkingSignaturesSummary, getActionGroupSummary, getThinkingSignatureRenderItemKey, groupThinkingSignaturesForRender } from '../thinkingSignatures';
import { StyledAccordionCopilotThinkingSignatureContentsContainer, StyledAccordionCopilotThinkingSignatureRow, StyledAnchorCopilotThinkingSignatureUrlOpenRow, StyledGridCopilotThinkingActionCardsStack, StyledGridCopilotThinkingSignatureCollapsedRow, StyledGridCopilotThinkingSignatureDetailsConnector, StyledGridCopilotThinkingSignatureDetailsConnectorColumn, StyledGridCopilotThinkingSignatureExpandedContent, StyledGridCopilotThoughtOnlyExpandedContent, StyledGridCopilotThinkingSignatureExpandedRow, StyledGridCopilotThinkingSignatureStaticRow, StyledGridCopilotThinkingSignatureStepColumn, StyledGridCopilotThinkingSignatureStepConnector, StyledGridCopilotThinkingSignatureStepNumber } from '../styles';
import { type CopilotThinkingSignature } from '../types';
import { secondsToTimeString } from '../utils';
import CopilotActionCard from './CopilotActionCard';
import CopilotSearchSourcesDetail from './CopilotSearchSourcesDetail';
interface ThinkingSignaturesPartProps {
  thinkingSignatures: CopilotThinkingSignature[];
  isThinkingInProgress: boolean;
}

/**
 * Keeps an accordion open while `shouldAutoExpand` is true and collapses it when that
 * becomes false, while still allowing manual re-expand until the next auto state change.
 * Adjusting state during render when the prop changes — see React “storing info from previous renders”.
 */
const useAutoExpandAccordion = shouldAutoExpand => {
  const $ = _c(3);
  const [expanded, setExpanded] = useState(shouldAutoExpand);
  const [previousShouldAutoExpand, setPreviousShouldAutoExpand] = useState(shouldAutoExpand);
  if (shouldAutoExpand !== previousShouldAutoExpand) {
    setPreviousShouldAutoExpand(shouldAutoExpand);
    setExpanded(shouldAutoExpand);
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = (_event, nextExpanded) => {
      setExpanded(nextExpanded);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const onExpandedChange = t0;
  let t1;
  if ($[1] !== expanded) {
    t1 = {
      expanded,
      onExpandedChange
    };
    $[1] = expanded;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const isThoughtOnlySignatures = (signatures: CopilotThinkingSignature[]): boolean => signatures.length > 0 && signatures.every(signature => signature.type === 'thought');
const renderThoughtContent = (content: ReactNode): ReactNode => typeof content === 'string' ? <Typography variant="body3">{content}</Typography> : content;

/**
 * Thought-only: same shell as the old "Explored" accordion (radii / surface), but the
 * summary is "Thought for …" and details are just the thought body — no step numbers
 * or nested row accordions.
 */
const ThoughtOnlyAccordion = t0 => {
  const $ = _c(17);
  const {
    signature,
    shouldAutoExpand
  } = t0;
  const {
    expanded,
    onExpandedChange
  } = useAutoExpandAccordion(shouldAutoExpand);
  const t1 = signature.title ?? "Thought";
  let t2;
  if ($[0] !== t1) {
    t2 = <strong>{t1}</strong>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== signature.timeInSeconds) {
    t3 = signature.timeInSeconds > 0 ? `for ${secondsToTimeString(signature.timeInSeconds)}` : "briefly";
    $[2] = signature.timeInSeconds;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <Typography variant="body3">{t2}{" "}{t3}</Typography>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== signature.content) {
    t5 = renderThoughtContent(signature.content);
    $[7] = signature.content;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <StyledGridCopilotThoughtOnlyExpandedContent>{t5}</StyledGridCopilotThoughtOnlyExpandedContent>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChevronDownIcon />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== expanded || $[13] !== onExpandedChange || $[14] !== t4 || $[15] !== t6) {
    t8 = <StyledAccordionCopilotThinkingSignatureContentsContainer expanded={expanded} onChange={onExpandedChange} Summary={t4} Details={t6} expandIcon={t7} disableGutters={true} />;
    $[12] = expanded;
    $[13] = onExpandedChange;
    $[14] = t4;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
};
const renderThinkingSignatureItems = (thinkingSignatures: CopilotThinkingSignature[], isThinkingInProgress: boolean): ReactNode[] => {
  const renderItems = groupThinkingSignaturesForRender(thinkingSignatures);
  return renderItems.map((item, index) => {
    const isLast = index === renderItems.length - 1;
    const isLatestActive = isLast && isThinkingInProgress;
    const key = getThinkingSignatureRenderItemKey(item);
    if (item.kind === 'action_group') {
      return <ActionGroupRow key={key} actions={item.actions} groupType={item.groupType} index={index} isLast={isLast} isLatestActive={isLatestActive} />;
    }
    return <ThinkingSignatureRow key={key} signature={item.signature} index={index} isLast={isLast} isLatestActive={isLatestActive} />;
  });
};
const ThinkingSignaturesPart = t0 => {
  const $ = _c(22);
  const {
    thinkingSignatures,
    isThinkingInProgress
  } = t0;
  const {
    expanded,
    onExpandedChange
  } = useAutoExpandAccordion(isThinkingInProgress);
  if (thinkingSignatures.length === 0) {
    return null;
  }
  if (isThoughtOnlySignatures(thinkingSignatures)) {
    let t1;
    if ($[0] !== isThinkingInProgress || $[1] !== thinkingSignatures) {
      let t2;
      if ($[3] !== isThinkingInProgress || $[4] !== thinkingSignatures.length) {
        t2 = (signature, index) => {
          if (signature.type !== "thought") {
            return null;
          }
          return <ThoughtOnlyAccordion key={signature.id} signature={signature} shouldAutoExpand={index === thinkingSignatures.length - 1 && isThinkingInProgress} />;
        };
        $[3] = isThinkingInProgress;
        $[4] = thinkingSignatures.length;
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      t1 = thinkingSignatures.map(t2);
      $[0] = isThinkingInProgress;
      $[1] = thinkingSignatures;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[6] !== t1) {
      t2 = <>{t1}</>;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] !== isThinkingInProgress || $[9] !== thinkingSignatures) {
    t1 = renderThinkingSignatureItems(thinkingSignatures, isThinkingInProgress);
    $[8] = isThinkingInProgress;
    $[9] = thinkingSignatures;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const signatureRows = t1;
  let t2;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <strong>Explored</strong>;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== thinkingSignatures) {
    t3 = constructThinkingSignaturesSummary(thinkingSignatures);
    $[12] = thinkingSignatures;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t3) {
    t4 = <Typography variant="body3">{t2} {t3}</Typography>;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronDownIcon />;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== expanded || $[18] !== onExpandedChange || $[19] !== signatureRows || $[20] !== t4) {
    t6 = <StyledAccordionCopilotThinkingSignatureContentsContainer expanded={expanded} onChange={onExpandedChange} Summary={t4} Details={signatureRows} expandIcon={t5} disableGutters={true} />;
    $[17] = expanded;
    $[18] = onExpandedChange;
    $[19] = signatureRows;
    $[20] = t4;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
};
const ThinkingSignatureSummary = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Grid display="flex" alignItems="center" paddingBottom="4px"><Typography variant="body3">{children}</Typography></Grid>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const ThinkingSignatureTimelineStep = t0 => {
  const $ = _c(7);
  const {
    index,
    isLast
  } = t0;
  const t1 = index + 1;
  let t2;
  if ($[0] !== t1) {
    t2 = <StyledGridCopilotThinkingSignatureStepNumber><Typography variant="body3">{t1}</Typography></StyledGridCopilotThinkingSignatureStepNumber>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = !isLast;
  let t4;
  if ($[2] !== t3) {
    t4 = <StyledGridCopilotThinkingSignatureStepConnector isVisible={t3} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== t4) {
    t5 = <StyledGridCopilotThinkingSignatureStepColumn>{t2}{t4}</StyledGridCopilotThinkingSignatureStepColumn>;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};
const ThinkingSignatureCollapsedRow = t0 => {
  const $ = _c(6);
  const {
    index,
    isLast,
    summary
  } = t0;
  let t1;
  if ($[0] !== index || $[1] !== isLast) {
    t1 = <ThinkingSignatureTimelineStep index={index} isLast={isLast} />;
    $[0] = index;
    $[1] = isLast;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== summary || $[4] !== t1) {
    t2 = <StyledGridCopilotThinkingSignatureCollapsedRow>{t1}{summary}</StyledGridCopilotThinkingSignatureCollapsedRow>;
    $[3] = summary;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const ThinkingSignatureExpandedDetails = t0 => {
  const $ = _c(7);
  const {
    isLast,
    children
  } = t0;
  const t1 = !isLast;
  let t2;
  if ($[0] !== t1) {
    t2 = <StyledGridCopilotThinkingSignatureDetailsConnectorColumn><StyledGridCopilotThinkingSignatureDetailsConnector isVisible={t1} /></StyledGridCopilotThinkingSignatureDetailsConnectorColumn>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <Grid display="flex" paddingBottom="12px" width="100%" minWidth={0} overflow="hidden">{children}</Grid>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <StyledGridCopilotThinkingSignatureExpandedRow>{t2}{t3}</StyledGridCopilotThinkingSignatureExpandedRow>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
const ThinkingSignatureAccordionRow = t0 => {
  const $ = _c(13);
  const {
    index,
    isLast,
    isLatestActive,
    summary,
    details
  } = t0;
  const {
    expanded,
    onExpandedChange
  } = useAutoExpandAccordion(isLatestActive);
  let t1;
  if ($[0] !== index || $[1] !== isLast || $[2] !== summary) {
    t1 = <ThinkingSignatureCollapsedRow index={index} isLast={isLast} summary={summary} />;
    $[0] = index;
    $[1] = isLast;
    $[2] = summary;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== details || $[5] !== isLast) {
    t2 = <ThinkingSignatureExpandedDetails isLast={isLast}>{details}</ThinkingSignatureExpandedDetails>;
    $[4] = details;
    $[5] = isLast;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronDownIcon />;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== expanded || $[9] !== onExpandedChange || $[10] !== t1 || $[11] !== t2) {
    t4 = <StyledAccordionCopilotThinkingSignatureRow expanded={expanded} onChange={onExpandedChange} Summary={t1} Details={t2} expandIcon={t3} disableGutters={true} />;
    $[8] = expanded;
    $[9] = onExpandedChange;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
const CopilotThinkingActionCard = t0 => {
  const $ = _c(4);
  const {
    action
  } = t0;
  const t1 = action.type === "action" ? action.onHighlight : undefined;
  let t2;
  if ($[0] !== action.detail || $[1] !== action.label || $[2] !== t1) {
    t2 = <CopilotActionCard label={action.label} detail={action.detail} onHighlight={t1} />;
    $[0] = action.detail;
    $[1] = action.label;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const ActionGroupRow = t0 => {
  const $ = _c(19);
  const {
    actions,
    groupType,
    index,
    isLast,
    isLatestActive
  } = t0;
  let t1;
  if ($[0] !== actions || $[1] !== groupType) {
    t1 = getActionGroupSummary(groupType, actions);
    $[0] = actions;
    $[1] = groupType;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    title,
    label
  } = t1;
  let t2;
  if ($[3] !== title) {
    t2 = <strong>{title}</strong>;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== actions.length || $[6] !== label || $[7] !== t2) {
    t3 = <ThinkingSignatureSummary>{t2} {actions.length} {label}</ThinkingSignatureSummary>;
    $[5] = actions.length;
    $[6] = label;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== actions) {
    t4 = actions.map(_temp);
    $[9] = actions;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <StyledGridCopilotThinkingActionCardsStack>{t4}</StyledGridCopilotThinkingActionCardsStack>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== index || $[14] !== isLast || $[15] !== isLatestActive || $[16] !== t3 || $[17] !== t5) {
    t6 = <ThinkingSignatureAccordionRow index={index} isLast={isLast} isLatestActive={isLatestActive} summary={t3} details={t5} />;
    $[13] = index;
    $[14] = isLast;
    $[15] = isLatestActive;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
const getThinkingSignatureSummary = (signature: NonActionThinkingSignature): ReactNode => {
  switch (signature.type) {
    case 'thought':
      return <ThinkingSignatureSummary>
          <strong>{signature.title ?? 'Thought'}</strong>{' '}
          {signature.timeInSeconds > 0 ? `for ${secondsToTimeString(signature.timeInSeconds)}` : 'briefly'}
        </ThinkingSignatureSummary>;
    case 'search':
      return <ThinkingSignatureSummary>
          <strong>{signature.title ?? 'Search'}</strong> for {signature.searchTerm}
        </ThinkingSignatureSummary>;
    case 'inspect_page':
      return <ThinkingSignatureSummary>
          <strong>{signature.title ?? 'Inspected page'}</strong> for {signature.description}
        </ThinkingSignatureSummary>;
    case 'inspect_source':
      return <ThinkingSignatureSummary>
          <strong>{signature.title ?? 'Discovered'}</strong> — {signature.description}
        </ThinkingSignatureSummary>;
    case 'url_open':
      return <ThinkingSignatureSummary>
          <strong>{signature.title ?? 'URL Open'}</strong> for {signature.url}
        </ThinkingSignatureSummary>;
  }
};
const ThinkingSignatureRow = t0 => {
  const $ = _c(34);
  const {
    signature,
    index,
    isLast,
    isLatestActive
  } = t0;
  let t1;
  if ($[0] !== signature) {
    t1 = getThinkingSignatureSummary(signature);
    $[0] = signature;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const summary = t1;
  let t2;
  if ($[2] !== index || $[3] !== isLast || $[4] !== summary) {
    t2 = <ThinkingSignatureCollapsedRow index={index} isLast={isLast} summary={summary} />;
    $[2] = index;
    $[3] = isLast;
    $[4] = summary;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const collapsedRow = t2;
  if (signature.type === "url_open") {
    const t3 = `Open link in new tab: ${signature.url}`;
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        flex: 1,
        minWidth: 0
      };
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== collapsedRow) {
      t5 = <div style={t4}>{collapsedRow}</div>;
      $[7] = collapsedRow;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Grid display="flex" alignItems="center" flexShrink={0} style={{
        paddingBottom: "4px"
      }}><LinkIcon /></Grid>;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] !== signature.url || $[11] !== t3 || $[12] !== t5) {
      t7 = <StyledAnchorCopilotThinkingSignatureUrlOpenRow href={signature.url} target="_blank" rel="noopener noreferrer" aria-label={t3}>{t5}{t6}</StyledAnchorCopilotThinkingSignatureUrlOpenRow>;
      $[10] = signature.url;
      $[11] = t3;
      $[12] = t5;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  if (signature.type === "search" && signature.searchDetails != null && signature.searchDetails.length > 0) {
    let t3;
    if ($[14] !== signature.searchDetails) {
      t3 = <StyledGridCopilotThinkingActionCardsStack><CopilotSearchSourcesDetail searchDetails={signature.searchDetails} /></StyledGridCopilotThinkingActionCardsStack>;
      $[14] = signature.searchDetails;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t4;
    if ($[16] !== index || $[17] !== isLast || $[18] !== isLatestActive || $[19] !== summary || $[20] !== t3) {
      t4 = <ThinkingSignatureAccordionRow index={index} isLast={isLast} isLatestActive={isLatestActive} summary={summary} details={t3} />;
      $[16] = index;
      $[17] = isLast;
      $[18] = isLatestActive;
      $[19] = summary;
      $[20] = t3;
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    return t4;
  }
  if (signature.type === "search" || signature.type === "inspect_page" || signature.type === "inspect_source") {
    let t3;
    if ($[22] !== collapsedRow) {
      t3 = <StyledGridCopilotThinkingSignatureStaticRow>{collapsedRow}</StyledGridCopilotThinkingSignatureStaticRow>;
      $[22] = collapsedRow;
      $[23] = t3;
    } else {
      t3 = $[23];
    }
    return t3;
  }
  let t3;
  if ($[24] !== signature.content) {
    t3 = typeof signature.content === "string" ? <Typography variant="body3">{signature.content}</Typography> : signature.content;
    $[24] = signature.content;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  let t4;
  if ($[26] !== t3) {
    t4 = <StyledGridCopilotThinkingSignatureExpandedContent>{t3}</StyledGridCopilotThinkingSignatureExpandedContent>;
    $[26] = t3;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  let t5;
  if ($[28] !== index || $[29] !== isLast || $[30] !== isLatestActive || $[31] !== summary || $[32] !== t4) {
    t5 = <ThinkingSignatureAccordionRow index={index} isLast={isLast} isLatestActive={isLatestActive} summary={summary} details={t4} />;
    $[28] = index;
    $[29] = isLast;
    $[30] = isLatestActive;
    $[31] = summary;
    $[32] = t4;
    $[33] = t5;
  } else {
    t5 = $[33];
  }
  return t5;
};
export default ThinkingSignaturesPart;
function _temp(action) {
  return <CopilotThinkingActionCard key={action.id} action={action} />;
}